"use client";

import { useEffect, useLayoutEffect, type RefObject } from "react";

/**
 * Grow a textarea with its content, between `min` and `max` pixels, then let it
 * scroll. Extracted 2026-09-18 when the docked mini bar needed the same
 * behaviour as the homepage hero, rather than a second copy of it.
 *
 * ⛔ HEIGHT IS RESET TO `auto` BEFORE MEASURING. A textarea's `scrollHeight`
 * never reports less than the height it is currently at, so without the reset
 * a field that grew could never shrink again as the text is deleted.
 *
 * ⛔ WIDTH DECIDES HOW MANY LINES THE TEXT TAKES, so a measurement made at one
 * width is wrong at the next. Measured 2026-09-18 on the hero: a question typed
 * at 812px stood at 72px, and rotating to 375px left it there, scrolling at
 * 96px of content while four lines of room went unused. The `ResizeObserver`
 * closes that; it is gated on the WIDTH actually changing, both because height
 * is what this hook itself writes (an ungated observer re-entering on its own
 * write is the classic RO loop) and because nothing else can change the line
 * count. Same dedup as `ProfessionalCard`'s name fitter.
 *
 * `fixed` pins the height instead, for a host with a collapsed state whose
 * height is a contract of its own (the mini bar's resting pill).
 */
export function useAutogrowTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  { value, min, max, fixed = null }: { value: string; min: number; max: number; fixed?: number | null },
) {
  // Measure before paint on the client; a no-op on the server, where there is
  // no layout to read. Avoids React's SSR `useLayoutEffect` warning.
  const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let lastWidth = -1;
    const apply = () => {
      lastWidth = el.clientWidth;
      if (fixed != null) {
        el.style.height = `${fixed}px`;
        return;
      }
      el.style.height = "auto";
      el.style.height = `${Math.min(Math.max(el.scrollHeight, min), max)}px`;
    };
    apply();
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return; // our own height write, or a no-op
      apply();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [useIsoLayoutEffect, ref, value, min, max, fixed]);
}
